import { Flex, Text } from "styles/utility-components";

import Button from "components/ui/button/Button";
import Modal from "components/ui/modal/Modal";
import toast from "components/ui/toast/Toast";
import type { McpClient } from "lib/hooks/api/auth/mcp/schemas";
import { useRevokeMcpClient } from "lib/hooks/api/auth/mcp/useRevokeMcpClient";

type Props = {
  credential: McpClient;
  onClose: () => void;
};

const BODY_ID = "revoke-mcp-credential-modal-body";

export function RevokeCredentialModal({ credential, onClose }: Props) {
  const { mutate: revokeCredential, isLoading } = useRevokeMcpClient({
    onSuccess: () => {
      toast.success("Credential revoked");
      onClose();
    },
    onError: (error) => toast.error(error.message ?? "Could not revoke the credential"),
  });

  return (
    <Modal
      open
      onOpenChange={onClose}
      maxWidth="440px"
      showCloseButton={false}
      aria-describedby={BODY_ID}
      padding={24}
    >
      <Flex column gap={24}>
        <Flex column gap={8}>
          <Text h2>Revoke credential</Text>
          <Text id={BODY_ID}>
            {credential.client_name ?? "This credential"} will stop working, and anyone in your
            company using it loses access to Stotles within 15 minutes. This can&apos;t be undone —
            reconnecting means generating a new credential and setting it up again.
          </Text>
        </Flex>
        <Flex justifyContent="flex-end" gap={8}>
          <Button buttonStyle="inverse" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            buttonStyle="destructive"
            onClick={() => revokeCredential({ clientId: credential.client_id })}
            disabled={isLoading}
            loading={isLoading}
            // The card carries a Revoke button too, so the visible label alone doesn't say which
            // one this is — to a screen reader or to a test.
            aria-label="Confirm revoke"
          >
            Revoke
          </Button>
        </Flex>
      </Flex>
    </Modal>
  );
}
